// resources/js/admin/hooks/cms/useMedia.ts

import { useState, useEffect, useCallback } from "react";
import useToken from "../auth/useToken";
import { Media, MediaFormData, MediaUploadData } from "../../../types";
import { mediaService } from "../../../services/mediaService";

export default function useMedia(siteSlug: string) {
  const { token } = useToken();
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchMedia = useCallback(async (page: number = 1, perPage: number = 20) => {
    if (!siteSlug) return;

    setLoading(true);
    setError(null);
    try {
      const response = await mediaService.getAll(token , siteSlug, page, perPage, filterType, searchTerm || undefined);
      setMedia(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des médias");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [siteSlug, filterType, searchTerm]);

  // 🔓 PUBLIC - Pas de token nécessaire
  const fetchMediaById = useCallback(async (id: string): Promise<Media | null> => {
    setLoading(true);
    setError(null);
    try {
      const mediaItem = await mediaService.getById(id);
      return mediaItem;
    } catch (err) {
      setError("Erreur lors du chargement du média");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔒 PROTÉGÉ - Token requis
  const uploadMedia = useCallback(async (data: MediaUploadData): Promise<Media | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const newMedia = await mediaService.upload(token, data);
      setMedia((prev) => [newMedia, ...prev]);
      return newMedia;
    } catch (err: any) {
      setError("Erreur lors du téléchargement du média");
      console.error(err.response);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const updateMedia = useCallback(async (id: string, data: MediaFormData): Promise<Media | null> => {
    if (!token) {
      setError("Vous devez être connecté");
      return null;
    }

    setLoading(true);
    setError(null);
    try {
      const updatedMedia = await mediaService.update(token, id, data);
      setMedia((prev) => prev.map((m) => (m.id === id ? updatedMedia : m)));
      return updatedMedia;
    } catch (err) {
      setError("Erreur lors de la mise à jour du média");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteMedia = useCallback(async (id: string): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await mediaService.delete(token, id);
      setMedia((prev) => prev.filter((m) => m.id !== id));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression du média");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 🔒 PROTÉGÉ - Token requis
  const deleteManyMedia = useCallback(async (ids: string[]): Promise<boolean> => {
    if (!token) {
      setError("Vous devez être connecté");
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      await mediaService.deleteMany(token, ids);
      setMedia((prev) => prev.filter((m) => !ids.includes(m.id)));
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Filtres
  const filterByType = useCallback((type: string | undefined) => {
    setFilterType(type);
  }, []);

  const searchMedia = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  return {
    media,
    loading,
    error,
    pagination,
    filterType,
    searchTerm,
    fetchMedia,
    fetchMediaById,
    uploadMedia,
    updateMedia,
    deleteMedia,
    deleteManyMedia,
    filterByType,
    searchMedia,
  };
}